import { calculate, roundFriendlyUp } from '@deizi/pricing';
import {
  Rounding as RoundingOptions,
  type Settings,
  formatBRL,
  formatDate,
  pricingCalculateExample,
} from '@deizi/shared';
import { useState } from 'react';
import { AffixInput, Field, SaveBar, Seg } from '../../../components/Form';
import { Icon } from '../../../components/Icon';
import { Alert } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { errorMessage } from '../../../lib/messages';
import { centavosInput, parseCentavos, parseDecimal } from '../../../lib/numbers';
import { useUpdateSettings } from '../../../lib/settings-api';
import { ROUNDING_LABELS } from './format';
import { HistoryPanel } from './HistoryPanel';

type Rules = Settings['rules'];
type Rounding = Rules['rounding'];

function leadDays(printHours: number, perDay: number): string {
  const days = Math.max(1, Math.ceil(printHours / Math.max(1, perDay)));
  return days === 1 ? '~1 dia' : `~${days} dias`;
}

/** Regras de preço tab: rounding (always up), minimum order, quote validity, print hours/day. */
export function RulesTab({ settings, readOnly }: { settings: Settings; readOnly: boolean }) {
  const saved = settings.rules;
  const [rounding, setRounding] = useState<Rounding>(saved.rounding);
  const [minOrder, setMinOrder] = useState(centavosInput(saved.minimumOrderCentavos));
  const [validity, setValidity] = useState(String(saved.quoteValidityDays));
  const [perDay, setPerDay] = useState(String(saved.printHoursPerDay));
  const [example, setExample] = useState('54,37');
  const update = useUpdateSettings();
  const toast = useToast();
  const [today] = useState(() => Date.now());

  const next: Partial<Rules> = {};
  const minOrderValue = parseCentavos(minOrder);
  const validityValue = Number.parseInt(validity, 10);
  const perDayValue = Number.parseInt(perDay, 10);
  if (rounding !== saved.rounding) next.rounding = rounding;
  if (minOrderValue !== null && minOrderValue !== saved.minimumOrderCentavos)
    next.minimumOrderCentavos = minOrderValue;
  if (validityValue > 0 && validityValue !== saved.quoteValidityDays)
    next.quoteValidityDays = validityValue;
  if (perDayValue > 0 && perDayValue <= 24 && perDayValue !== saved.printHoursPerDay)
    next.printHoursPerDay = perDayValue;
  const dirty = Object.keys(next).length > 0;

  const exampleCentavos = Math.round((parseDecimal(example) ?? 0) * 100);
  const validUntil = new Date(
    today + (validityValue > 0 ? validityValue : saved.quoteValidityDays) * 86_400_000,
  );

  // "Como o preço sai": Vaso Ondas M on Direto · Pix with the example costs.
  const pix = calculate({
    ...pricingCalculateExample,
    rounding,
    channels: [
      {
        id: 'pix',
        name: 'Direto · Pix',
        commissionBps: 0,
        paymentFeeBps: 0,
        fixedFees: [],
        shipping: null,
      },
    ],
  });
  const pixChannel = pix.channels[0];

  const reset = () => {
    setRounding(saved.rounding);
    setMinOrder(centavosInput(saved.minimumOrderCentavos));
    setValidity(String(saved.quoteValidityDays));
    setPerDay(String(saved.printHoursPerDay));
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <div className="col" style={{ flex: '3 1 520px', minWidth: 0, gap: 18 }}>
        <section className="card col" style={{ gap: 14 }}>
          <h2>Arredondamento do preço sugerido</h2>
          <Seg
            label="Arredondamento"
            value={rounding}
            onChange={setRounding}
            disabled={readOnly}
            options={RoundingOptions.options.map((v) => ({
              value: v,
              label: ROUNDING_LABELS[v] ?? v,
            }))}
          />
          <div
            className="card sunk row wrap"
            style={{ gap: 18, padding: 14, alignItems: 'flex-end' }}
            aria-live="polite"
          >
            <Field id="ex" label="Preço calculado" style={{ width: 170 }}>
              <AffixInput id="ex" prefix="R$" value={example} onChange={setExample} />
            </Field>
            <Icon name="chevronRight" />
            <div>
              <p className="lbl">Vira</p>
              <p className="fr tab" style={{ fontSize: 26, color: 'var(--pe)' }}>
                {formatBRL(roundFriendlyUp(exampleCentavos, rounding))}
              </p>
            </div>
            <div className="grow" />
            <div className="col sm" style={{ gap: 2 }}>
              <span className="muted">
                R$ 36,12 →{' '}
                <b className="tab" style={{ color: 'var(--ink)' }}>
                  {formatBRL(roundFriendlyUp(3612, rounding))}
                </b>
              </span>
              <span className="muted">
                R$ 59,95 →{' '}
                <b className="tab" style={{ color: 'var(--ink)' }}>
                  {formatBRL(roundFriendlyUp(5995, rounding))}
                </b>
              </span>
            </div>
          </div>
          <p className="xs muted">
            Sempre arredonda para cima: o preço nunca fica abaixo do calculado.
          </p>
        </section>

        <section className="card col" style={{ gap: 16 }}>
          <h2>Pedido e orçamento</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))',
              gap: 14,
            }}
          >
            <Field
              id="mp"
              label="Preço mínimo por pedido"
              help="Abaixo disso o orçamento avisa e sugere completar."
            >
              <AffixInput
                id="mp"
                prefix="R$"
                value={minOrder}
                onChange={setMinOrder}
                disabled={readOnly}
                changed={'minimumOrderCentavos' in next}
                invalid={minOrderValue === null}
              />
            </Field>
            <Field
              id="va"
              label="Validade do orçamento"
              help={`Orçamento feito hoje vale até ${formatDate(validUntil)}.`}
            >
              <AffixInput
                id="va"
                unit="dias"
                inputMode="numeric"
                value={validity}
                onChange={setValidity}
                disabled={readOnly}
                changed={'quoteValidityDays' in next}
                invalid={!(validityValue > 0)}
              />
            </Field>
          </div>
        </section>

        <section className="card col" style={{ gap: 16 }}>
          <h2>Prazo de produção</h2>
          <div style={{ maxWidth: 260 }}>
            <Field id="hd" label="Horas de impressão por dia">
              <AffixInput
                id="hd"
                unit="h/dia"
                inputMode="numeric"
                value={perDay}
                onChange={setPerDay}
                disabled={readOnly}
                changed={'printHoursPerDay' in next}
                invalid={!(perDayValue > 0 && perDayValue <= 24)}
              />
            </Field>
          </div>
          <div className="card sunk col" style={{ gap: 4, padding: 14 }} aria-live="polite">
            {[
              ['Pedido com 6h20 de impressão', 6.33],
              ['Pedido com 12h40 de impressão', 12.67],
              ['Pedido com 30 h de impressão', 30],
            ].map(([label, hours]) => (
              <div key={String(label)} className="kv">
                <span className="muted">{label}</span>
                <span className="v">
                  {leadDays(Number(hours), perDayValue > 0 ? perDayValue : saved.printHoursPerDay)}
                </span>
              </div>
            ))}
          </div>
          <p className="xs muted">
            Quando a fila de impressão existir, o prazo soma o que já está na fila.
          </p>
        </section>

        {update.isError ? <Alert tone="err">{errorMessage(update.error)}</Alert> : null}
        {dirty && !readOnly ? (
          <SaveBar
            label="Regras alteradas."
            detail="Valem para orçamentos novos; os já enviados não mudam."
            saveLabel="Salvar regras"
            saving={update.isPending}
            onDiscard={reset}
            onSave={() =>
              update.mutate(
                { rules: next },
                { onSuccess: () => toast('Regras salvas. Valem para orçamentos novos.') },
              )
            }
          />
        ) : null}
      </div>
      <div className="col" style={{ flex: '2 1 300px', minWidth: 0, gap: 18 }}>
        <aside className="card col" style={{ gap: 10 }}>
          <h2>Como o preço sai</h2>
          <div className="kv">
            <span className="muted">1. Custo da peça</span>
            <span className="v">{formatBRL(pix.cost.unitCost)}</span>
          </div>
          <div className="kv">
            <span className="muted">2. Margem alvo e tarifas do canal</span>
            <span className="v">Pix {formatBRL(pixChannel?.suggested?.rawCentavos ?? 0)}</span>
          </div>
          <div className="kv">
            <span className="muted">3. Arredondamento</span>
            <span className="v">{formatBRL(pixChannel?.suggested?.priceCentavos ?? 0)}</span>
          </div>
          <p className="xs muted">Exemplo: Vaso Ondas M no Direto · Pix.</p>
        </aside>
        <HistoryPanel filter="rules" />
      </div>
    </div>
  );
}
